declare const Handlebars: {
  compile: (
    template: string,
    options?: {
      strict?: boolean;
    },
  ) => (data: Record<string, unknown>) => string;
};

const removeBrokenFromString = (data: string) => {
  let content = data;

  const values = [];
  const matches = content.match(/\{{1,}[a-zA-Z0-9_.-]+\}{1,}/gm) ?? [];
  for (let i = 0; i < matches.length; i++) {
    const match = matches[i];

    let open = 0;
    let close = 0;
    for (const char of match) {
      if (char === "{") open++;
      else if (char === "}") close++;
    }

    if (open === close) {
      const idx = values.push(match);
      content = content.replace(match, `[[([${idx - 1}])]]`);
    } else {
      content = content.replace(match, "");
    }
  }

  content = content.replace(
    /\{{1,}[a-zA-Z0-9_.-]+\}{0,1}|\{[a-zA-Z0-9_.-]+\}{2,}/gm,
    "",
  );

  for (let i = 0; i < values.length; i++) {
    content = content.replace(`[[([${i}])]]`, values[i]);
  }

  return content;
};

const sanitizeJson = (value: unknown): unknown => {
  if (typeof value === "string") {
    return removeBrokenFromString(value);
  }
  if (Array.isArray(value)) {
    return value.map(sanitizeJson);
  }
  if (value !== null && typeof value === "object") {
    const sanitizedObj: Record<string, unknown> = {};
    for (const key in value) {
      if (Object.hasOwn(value, key)) {
        sanitizedObj[key] = sanitizeJson(value[key as keyof typeof value]);
      }
    }
    return sanitizedObj;
  }

  return value;
};

const removeInvalidPlaceholders = (content: string): string => {
  try {
    const parsed = JSON.parse(content);
    return JSON.stringify(sanitizeJson(parsed));
  } catch {
    return removeBrokenFromString(content);
  }
};

// biome-ignore lint/suspicious/noControlCharactersInRegex: matching raw JSON-breaking control chars is the intent
const CONTROL_CHARS_REGEX = /[\u0000-\u001f]/g;

const escapeControlChars = (value: string): string =>
  value.replace(CONTROL_CHARS_REGEX, (char) => {
    switch (char) {
      case "\b":
        return "\\b";
      case "\f":
        return "\\f";
      case "\n":
        return "\\n";
      case "\r":
        return "\\r";
      case "\t":
        return "\\t";
      default:
        return `\\u${char.charCodeAt(0).toString(16).padStart(4, "0")}`;
    }
  });

/**
 * Deep-escapes raw control characters (newlines, tabs, etc.) in every string
 * value of a Handlebars data object into their JSON escape sequences.
 *
 * Use this before compiling a *stringified-JSON* template
 * (`JSON.parse(safeCompile(JSON.stringify(obj), data))`): Handlebars substitutes
 * variable values verbatim, so a value containing a literal newline lands
 * unescaped inside a JSON string literal and makes the subsequent `JSON.parse`
 * throw "Bad control character in string literal". Escaping here keeps the JSON
 * valid; the value round-trips back to its original form after parsing.
 *
 * Safe for both `{{ }}` (HTML-escaped) and `{{{ }}}` (raw) placeholders, since
 * Handlebars never HTML-escapes control characters or the resulting `\n` escapes.
 */
export const escapeJsonControlChars = (value: unknown): unknown => {
  if (typeof value === "string") {
    return escapeControlChars(value);
  }
  if (Array.isArray(value)) {
    return value.map(escapeJsonControlChars);
  }
  if (value !== null && typeof value === "object") {
    const result: Record<string, unknown> = {};
    for (const key in value) {
      if (Object.hasOwn(value, key)) {
        result[key] = escapeJsonControlChars(value[key as keyof typeof value]);
      }
    }
    return result;
  }
  return value;
};

function safeCompile(content: string, data: Record<string, unknown>) {
  if (typeof content !== "string") {
    throw new Error(
      "safeCompile received non-string content, returning empty string.",
    );
  }

  try {
    const template = Handlebars.compile(content);
    return template(data);
  } catch (e) {
    console.log(e);
    const cleanedContent = removeInvalidPlaceholders(content);
    const compiledTemplate = Handlebars.compile(cleanedContent, {
      strict: false,
    });
    return compiledTemplate(data);
  }
}

export default safeCompile;
